/**
 * The reasoning-effort ladder, shared by the template editor (which picks a level) and the
 * invocation log (which reports the one a run asked for).
 *
 * Wire values are the camelCase serialization of the backend `ReasoningEffort` enum. "xhigh" is
 * pinned server-side rather than following the naming policy, so every level happens to be
 * lowercase — but per CLAUDE.md, never rely on that: compare case-insensitively.
 */
export const REASONING_LEVELS = [
  "none",
  "minimal",
  "low",
  "medium",
  "high",
  "xhigh",
  "max",
];

const LABELS = {
  none: "None",
  minimal: "Minimal",
  low: "Low",
  medium: "Medium",
  high: "High",
  xhigh: "Extra high",
  max: "Max",
};

const norm = (level) => (level || "").toLowerCase();

export const reasoningRank = (level) => REASONING_LEVELS.indexOf(norm(level));

export function reasoningLabel(level) {
  return LABELS[norm(level)] || level || null;
}

export function findModelDescriptor(providers, modelName) {
  if (!modelName) return null;
  return (
    providers
      ?.flatMap((provider) => provider.models || [])
      ?.find((m) => m.name === modelName) || null
  );
}

/**
 * Reconciles the effort a request asked for against what the serving model actually accepts.
 *
 * The stored value is the *requested* level; providers clamp it into the model's accepted
 * range (`MinReasoningEffort` … `MaxReasoningEffort`) rather than rejecting it, and drop it
 * entirely for models with no reasoning control. So the log has to say which of the four
 * happened — reporting the stored value alone would claim a depth that was never served.
 *
 * @returns null when nothing was requested, otherwise
 *   `{ requested, label, effective, effectiveLabel, state, note }` where state is one of
 *   "asRequested" | "clamped" | "raised" | "ignored" | "unknownModel".
 */
export function describeReasoningEffort(effort, modelName, providers) {
  if (!effort) return null;

  const requested = norm(effort);
  const label = reasoningLabel(requested);
  const model = findModelDescriptor(providers, modelName);

  if (model && model.supportsReasoningEffort === false) {
    return {
      requested,
      label,
      effective: null,
      effectiveLabel: null,
      state: "ignored",
      note: `${modelName} has no reasoning control — this level was ignored.`,
    };
  }

  const ceiling = norm(model?.maxReasoningEffort);
  if (model && ceiling && reasoningRank(requested) > reasoningRank(ceiling)) {
    return {
      requested,
      label,
      effective: ceiling,
      effectiveLabel: reasoningLabel(ceiling),
      state: "clamped",
      note: `Requested ${label}, but ${modelName} tops out at ${reasoningLabel(ceiling)} — the call was clamped, not rejected.`,
    };
  }

  const floor = norm(model?.minReasoningEffort);

  // GPT-6 Sol and Luna accept "none" and then low and above. "minimal" sits in that
  // span and is a 400, so the catalog flags it and the call is raised to low. A floor
  // that is already above minimal is handled by the range check below.
  if (
    model?.rejectsMinimalReasoning &&
    requested === "minimal" &&
    reasoningRank(requested) >= reasoningRank(floor || "none")
  ) {
    const raisedTo =
      floor && reasoningRank(floor) > reasoningRank("low") ? floor : "low";
    return {
      requested,
      label,
      effective: raisedTo,
      effectiveLabel: reasoningLabel(raisedTo),
      state: "raised",
      note: `Requested ${label}, but ${modelName} does not accept minimal — the call was raised to ${reasoningLabel(raisedTo)}.`,
    };
  }

  if (model && floor && reasoningRank(requested) < reasoningRank(floor)) {
    return {
      requested,
      label,
      effective: floor,
      effectiveLabel: reasoningLabel(floor),
      state: "raised",
      note: `Requested ${label}, but ${modelName} starts at ${reasoningLabel(floor)} — the call was raised, not rejected.`,
    };
  }

  if (!model) {
    // A routing alias, a retired model, or one this deployment no longer serves: the range is
    // unknowable here, so claim only what was asked for.
    return {
      requested,
      label,
      effective: requested,
      effectiveLabel: label,
      state: "unknownModel",
      note: `Requested ${label}. The serving model's accepted range is not in the catalog, so a level outside it may have been clamped.`,
    };
  }

  return {
    requested,
    label,
    effective: requested,
    effectiveLabel: label,
    state: "asRequested",
    note: `Reasoning effort: ${label}.`,
  };
}
